import { AfterViewInit, Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize, map, switchMap, tap } from 'rxjs/operators';
import { ApAlertService, ApBaseComponent } from 'webcommon/core-ui';
import { ApEditorMessageButtonsDto, ApMessageService, ApMessageToken } from 'webcommon/messages';
import { ApEditMessageDto, ApReplyMessageDto, ApThreadMessageDto } from 'webcommon/shared';

// This is meant to be reused in PRM and Facility portal so the code needs to be fairly generic
@Component({
  providers: [ ApAlertService ],
  selector: 'fp-message-reply',
  templateUrl: './fp-message-reply.component.html',
})
export class FPMessageReplyComponent extends ApBaseComponent implements OnInit, AfterViewInit {
  @Input() baseId?: string;
  @Input() readOnly: boolean;
  @Input() messageId: string;
  @Input() alertWarnings: string[];
  @Input() showButtons?: ApEditorMessageButtonsDto;
  @Input() reloadOnSend: boolean;

  private _urgencyId: string | null = null;
  @Input()
  set urgencyId(val) {
    this._urgencyId = val;
  }
  get urgencyId() {
    return this._urgencyId;
  }
  @Output() urgencyIdChange = new EventEmitter<string | null>();

  private _dueDate: Date | null = null;
  @Input()
  set dueDate(val) {
    this._dueDate = val;
  }
  get dueDate() {
    return this._dueDate;
  }
  @Output() dueDateChange = new EventEmitter<Date | null>();

  @Output() dataSaved: EventEmitter<boolean> = new EventEmitter<boolean>();

  Id: string;
  hasError = false;
  errorMessage = '';
  messageThread: ApThreadMessageDto[];

  constructor(
    private alertService: ApAlertService,
    @Inject(ApMessageToken) private messageService: ApMessageService,
  ) {
    super();
  }

  ngOnInit() {
    this.loading = true;
    this.setId();
    this.load();
  }

  ngAfterViewInit(): void {
    if (this.alertWarnings && this.alertWarnings.length) {
      this.alertWarnings.forEach((warning) => {
        this.displayWarning(warning);
      });
    }
  }

  private load(): void {
    this.hasError = false;
    this.loading = true;

    this.messageService.getMessageThread(this.messageId).pipe(
      tap((result) => this.onMessageThreadLoadSuccess(result)),
      switchMap((result) => this.messageService.setMessagesRead(this.messageId, result.length)),
      finalize(() => this.onLoadComplete()),
      this.takeUntilDestroy(),
    ).subscribe(
      () => { /*Success*/ },
      () => this.onLoadError(),
    );
  }

  private reload(): void {
    this.load();
  }

  private onLoadComplete() {
    this.loading = false;
  }

  private onMessageThreadLoadSuccess(threadList: ApThreadMessageDto[]) {
    this.messageThread = threadList;
  }

  private onLoadError() {
    this.hasError = true;
  }

  private setId() {
    this.Id = `${this.baseId}-message-reply`;
  }

  private map(editMessageRequest: ApEditMessageDto) {
    const messageRequest: ApReplyMessageDto = {
      Body: editMessageRequest.Body,
      DueDate: editMessageRequest.DueDate,
      MessageId: this.messageId,
      UrgencyId: editMessageRequest.UrgencyId,
    };
    return messageRequest;
  }

  onMessageUrgencyChange(urgencyIdList: string | null) {
    this.urgencyIdChange.emit(urgencyIdList);
  }

  onMessageDueDateChange(value: Date | null) {
    this.dueDateChange.emit(value);
  }

  onComplete = (completeMessageRequest: ApEditMessageDto): Observable<boolean> => {
    return this.messageService.postCompleteMessage(this.map(completeMessageRequest))
      .pipe(
        catchError((error: unknown) => {
          this.hasError = true;
          return throwError(error);
        }),
        map(() => true),
      );
  };

  onSend = (replyMessageRequest: ApEditMessageDto): Observable<boolean> => {
    return this.messageService.postReplyMessage(this.map(replyMessageRequest))
      .pipe(
        catchError((error: unknown) => {
          this.hasError = true;
          return throwError(error);
        }),
        tap(() => {
          if (this.reloadOnSend) { this.reload(); }
          this.load();
        }),
        map(() => true),
      );
  };

  onEditComplete(dataSaved: boolean) {
    this.dataSaved.emit(dataSaved);
  }

  private displayWarning(text: string) {
    this.alertService.warn(text);
  }
}
